import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import { UserGroupState, UserGroup } from './types';
import { User } from '../user/types';
import * as types from './mutation-types';

// 更新・削除・所属ユーザー更新のエラーをクリアする
const resetChangesAllErrors = (state: UserGroupState): void => {
    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const addUsersState = state.addUsers;
    addUsersState.error = false;
    addUsersState.errorTitle = undefined;
    addUsersState.errorDetails = undefined;

    const removeUsersState = state.removeUsers;
    removeUsersState.error = false;
    removeUsersState.errorTitle = undefined;
    removeUsersState.errorDetails = undefined;
};

export const mutations: MutationTree<UserGroupState> = {
    // ユーザーグループ検索系
    [types.SEARCH_START](state) {
        const searchState = state.search;
        searchState.error = false;
        searchState.errorCode = undefined;
        searchState.errorMsg = undefined;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; user_groups: Array<UserGroup> }) {
        state.userGroupCount = payload.count;
        state.userGroups = payload.user_groups;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // ユーザーグループ作成系
    [types.NEW_USER_GROUP](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // ユーザーグループ詳細情報参照系
    [types.SHOW_START](state) {
        resetChangesAllErrors(state);
        const showState = state.show;
        showState.error = false;
        showState.errorCode = undefined;
        showState.errorMsg = undefined;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: { group: UserGroup; user: Array<User> }) {
        state.userGroupDetails.group = payload.group;
        state.userGroupDetails.users = payload.user.length > 0 ? payload.user : [];

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // ユーザーグループ更新系
    [types.UPDATE_START](state) {
        resetChangesAllErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // ユーザーグループ削除系
    [types.DESTROY_START](state) {
        resetChangesAllErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // 所属ユーザー追加系
    [types.NEW_USERS](state) {
        const addUsersState = state.addUsers;
        addUsersState.error = false;
        addUsersState.errorTitle = undefined;
        addUsersState.errorDetails = undefined;
    },

    [types.ADD_USERS_START](state) {
        resetChangesAllErrors(state);
        const addUsersState = state.addUsers;
        addUsersState.isProcessing = true;
    },

    [types.ADD_USERS_SUCCESS](state) {
        const addUsersState = state.addUsers;
        addUsersState.isProcessing = false;
    },

    [types.ADD_USERS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const addUsersState = state.addUsers;
        addUsersState.errorTitle = payload.errorTitle;
        addUsersState.errorDetails = payload.errorDetails;
        addUsersState.error = true;
        addUsersState.isProcessing = false;
    },

    // 所属ユーザー削減系
    [types.REMOVE_USERS_START](state) {
        resetChangesAllErrors(state);
        const removeUsersState = state.removeUsers;
        removeUsersState.isProcessing = true;
    },

    [types.REMOVE_USERS_SUCCESS](state) {
        const removeUsersState = state.removeUsers;
        removeUsersState.isProcessing = false;
    },

    [types.REMOVE_USERS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const removeUsersState = state.removeUsers;
        removeUsersState.errorTitle = payload.errorTitle;
        removeUsersState.errorDetails = payload.errorDetails;
        removeUsersState.error = true;
        removeUsersState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetChangesAllErrors(state);
        const csvState = state.csv;
        csvState.error = false;
        csvState.errorCode = undefined;
        csvState.errorMsg = undefined;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { code: string; message: string }) {
        const csvState = state.csv;
        csvState.errorCode = payload.code;
        csvState.errorMsg = payload.message;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const addUsersState = state.addUsers;
        addUsersState.isProcessing = false;

        const removeUsersState = state.removeUsers;
        removeUsersState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        resetChangesAllErrors(state);
        state.userGroups = undefined;
        state.userGroupDetails.group = undefined;
        state.userGroupDetails.users = undefined;
        state.userGroupCount = 0;
    },
};
